import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import type { BrandResponse } from "@/api";
import { createBrandMutation, getBrandQueryKey, listBrandsQueryKey, schemas, updateBrandMutation } from "@/api";
import { EntityFormCard, formText, IdentityFields } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import type { BrandDraft } from "./BrandExtractCard";

const update = schemas.zBrandUpdate.shape;

// Overview: identity only. The palette/fonts/tone live on the Brand kit tab.
const overviewSchema = z.object({
  name: update.name.unwrap().unwrap(),
  description: update.description,
  audience: update.audience,
});
type OverviewValues = z.infer<typeof overviewSchema>;

function draftKit(draft?: BrandDraft): Record<string, unknown> | undefined {
  if (!draft) return undefined;
  return {
    palette: {
      primary: draft.primary || undefined,
      secondary: draft.secondary || undefined,
      background: draft.bg || undefined,
      text: draft.text || undefined,
      accents: draft.accents
        ? draft.accents
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    },
    fonts: { heading: draft.headingFont || undefined, body: draft.bodyFont || undefined },
    tone: draft.tone || undefined,
  };
}

/**
 * The brand Overview form — the create page and the detail tab share it. On create, `draft` pre-fills it
 * (from a guidelines document, or a Build with AI card with its `brandStyle` and source chat).
 */
export function BrandForm({
  brand,
  draft,
  brandStyle,
  sourceSessionUuid,
}: Readonly<{ brand?: BrandResponse; draft?: BrandDraft; brandStyle?: string; sourceSessionUuid?: string }>) {
  const { t } = useTranslation("brands");
  const navigate = useNavigate();
  const text = formText(t, t);
  const { saveExisting, createNew } = useEntitySave(text.messages);

  const create = useMutation(createBrandMutation());
  const updateMutation = useMutation(updateBrandMutation());

  const form = useAppForm({
    defaultValues: {
      name: brand?.name ?? draft?.name ?? "",
      description: brand?.description ?? draft?.description ?? "",
      audience: brand?.audience ?? draft?.audience ?? "",
    } as OverviewValues,
    validators: { onChange: overviewSchema },
    onSubmit: ({ value }) => {
      const base = {
        name: value.name,
        description: value.description || null,
        audience: value.audience || null,
      };
      if (brand) {
        return saveExisting({
          request: () =>
            updateMutation.mutateAsync({ path: { brand_uuid: brand.uuid }, body: base, throwOnError: true }),
          invalidate: [listBrandsQueryKey(), getBrandQueryKey({ path: { brand_uuid: brand.uuid } })],
          onSaved: () => form.reset(value),
        });
      }
      return createNew({
        // On create, carry an extracted kit through to the new brand.
        request: async () =>
          (
            await create.mutateAsync({
              body: { ...base, brandKit: draftKit(draft), brandStyle, sourceSessionUuid },
              throwOnError: true,
            })
          ).data.uuid,
        invalidate: [listBrandsQueryKey()],
        to: (uuid) => `/build/brands/${uuid}`,
      });
    },
  });

  return (
    <EntityFormCard label={t("form.label")} onSubmit={() => void form.handleSubmit()}>
      <IdentityFields form={form} fields={{ name: "name", description: "description" }} labels={text.labels} />
      <form.AppField name="audience">
        {(field) => <field.TextField label={t("form.audienceLabel")} placeholder={t("form.audiencePlaceholder")} />}
      </form.AppField>
      {brand && (
        <Text variant="caption" tone="tertiary">
          {t("kit.editNote")}
        </Text>
      )}
      <form.AppForm>
        <form.SubmitActions
          onCancel={() => (brand ? form.reset() : navigate("/build/brands"))}
          submitLabel={brand ? undefined : t("form.create")}
          requireChanges={Boolean(brand)}
        />
      </form.AppForm>
    </EntityFormCard>
  );
}
